import { Suspense, lazy, ComponentType } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { DriverAuthProvider, useDriverAuth } from "@/hooks/useDriverAuth";
import { PermissionsGateProvider } from "@/hooks/usePermissionsGate";
import { NetworkGateProvider } from "@/hooks/useNetworkGate";
import { DriverThemeProvider } from "@/hooks/useDriverTheme";
import { LanguageProvider } from "@/hooks/useLanguage";
import { useRealtimeHealth } from "@/hooks/useRealtimeHealth";
import { useDriverPresence } from "@/hooks/useDriverPresence";
import { DeviceStatusProvider } from "@/hooks/useDeviceStatusBroadcast";
import { useEffect } from "react";
import { ErrorBoundary } from "@/components/ErrorBoundary";

import { Loader2 } from "lucide-react";
import GlobalTripOfferOverlay from "@/components/driver/GlobalTripOfferOverlay";
import SystemAnnouncementOverlay from "@/components/driver/SystemAnnouncementOverlay";
import AppPermissionsGate from "@/components/AppPermissionsGate";
import AppVersionGate from "@/components/AppVersionGate";
import { initWakeLock } from "@/hooks/useScreenWakeLock";
import { useFcmRegistration } from "@/hooks/useFcmRegistration";
import { useDriverNotifications } from "@/hooks/useDriverNotifications";
import { useActiveTripWatcher } from "@/hooks/useActiveTripWatcher";
import { registerBackHandler } from "@/hooks/useBackHandlerStack";
import { Capacitor } from "@capacitor/core";

// Retry wrapper: auto-reloads ONCE on a true chunk-load failure, then
// clears the guard so a later success does not leave the app stuck in a
// reload loop on low-RAM devices.
function lazyRetry(factory: () => Promise<{ default: ComponentType<any> }>) {
  return lazy(() =>
    factory().then((mod) => {
      try { sessionStorage.removeItem("chunk_reload"); } catch { /* ignore */ }
      return mod;
    }).catch((err) => {
      const key = "chunk_reload";
      const msg = String(err?.message || err || "");
      const isChunkErr = /chunk|dynamically imported|Failed to fetch/i.test(msg);
      if (isChunkErr && !sessionStorage.getItem(key)) {
        try { sessionStorage.setItem(key, "1"); } catch { /* ignore */ }
        setTimeout(() => window.location.reload(), 0);
      }
      throw err;
    })
  );
}

const NotFound = lazyRetry(() => import("./pages/NotFound"));

// Driver App
const DriverLogin = lazyRetry(() => import("./pages/driver/DriverLogin"));
const DriverRegister = lazyRetry(() => import("./pages/driver/DriverRegister"));
const DriverHome = lazyRetry(() => import("./pages/driver/DriverHome"));
const DriverTrips = lazyRetry(() => import("./pages/driver/DriverTrips"));

const DriverProfile = lazyRetry(() => import("./pages/driver/DriverProfile"));
const DriverPerformance = lazyRetry(() => import("./pages/driver/DriverPerformance"));
const DriverNotifications = lazyRetry(() => import("./pages/driver/DriverNotifications"));
const DriverSettings = lazyRetry(() => import("./pages/driver/DriverSettings"));
const DriverAbout = lazyRetry(() => import("./pages/driver/DriverAbout"));
const DriverPrivacyPolicy = lazyRetry(() => import("./pages/driver/DriverPrivacyPolicy"));
const DriverTerms = lazyRetry(() => import("./pages/driver/DriverTerms"));
const DriverDeleteAccount = lazyRetry(() => import("./pages/driver/DriverDeleteAccount"));
const DriverSelectVehicle = lazyRetry(() => import("./pages/driver/DriverSelectVehicle"));

const queryClient = new QueryClient();

function PageLoader() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
    </div>
  );
}



function AppRoutes() {
  return (
    <main className="min-h-dvh overflow-x-hidden">
      <Suspense fallback={<PageLoader />}>
        <VehiclePairingGuard />
        <Routes>
          <Route path="/" element={<Navigate to="/driver/login" replace />} />
          <Route path="/login" element={<Navigate to="/driver/login" replace />} />

          {/* Driver App Routes */}
          <Route path="/driver/login" element={<DriverLogin />} />
          <Route path="/driver/register" element={<DriverRegister />} />
          <Route path="/driver/select-vehicle" element={<DriverSelectVehicle />} />
          <Route path="/driver/home" element={<DriverHome />} />
          <Route path="/driver/trips" element={<DriverTrips />} />
          
          <Route path="/driver/profile" element={<DriverProfile />} />
          <Route path="/driver/performance" element={<DriverPerformance />} />
          <Route path="/driver/notifications" element={<DriverNotifications />} />
          <Route path="/driver/settings" element={<DriverSettings />} />
          <Route path="/driver/about" element={<DriverAbout />} />
          <Route path="/driver/privacy" element={<DriverPrivacyPolicy />} />
          <Route path="/driver/terms" element={<DriverTerms />} />
          <Route path="/driver/delete-account" element={<DriverDeleteAccount />} />

          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </main>
  );
}

// Redirects any authenticated driver without a paired vehicle to the
// vehicle-selection screen. Runs on every route change.
function VehiclePairingGuard() {
  const { driver, loading } = useDriverAuth();
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    if (loading || !driver) return;
    if (driver.vehicle_id) return;
    const path = location.pathname;
    const allow = [
      '/driver/login',
      '/driver/register',
      '/driver/select-vehicle',
      '/driver/terms',
      '/driver/privacy',
      '/driver/delete-account',
    ];
    if (allow.includes(path)) return;
    navigate('/driver/select-vehicle', { replace: true });
  }, [driver, loading, location.pathname, navigate]);
  return null;
}

// Global Android hardware-back / back-swipe handler. Mirrors the in-app
// top-left back arrow: pops the router history, or falls back to
// /driver/home when there is nothing to pop. On /driver/home and
// /driver/login it returns false so the higher-priority exit-guard on
// Home (double-back-to-exit) handles the event.
function GlobalBackNavigation() {
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    const unregister = registerBackHandler('router-back', 1, () => {
      const path = location.pathname;
      if (path === '/driver/home' || path === '/driver/login' || path === '/') {
        return false;
      }
      if (window.history.length > 1) {
        navigate(-1);
      } else {
        navigate('/driver/home', { replace: true });
      }
      return true;
    });
    return unregister;
  }, [location.pathname, navigate]);
  return null;
}

function GlobalRealtimeManagers() {
  // Activate wake lock once after the app has mounted — keeps it out of
  // the synchronous cold-start path so the first paint is not delayed by
  // the wake-lock plugin import on low-RAM devices.
  useEffect(() => { initWakeLock(); }, []);
  // P0: monitor & auto-reconnect Realtime socket on visibility/online events
  useRealtimeHealth();
  // P2: presence channel — admin sees live driver socket connections
  const { driver } = useDriverAuth();
  useDriverPresence(driver?.id, driver?.is_available ?? false);
  // App-wide FCM token REGISTRATION + push handling. Both mounted here so:
  //  • Token registration runs whenever a driver session exists,
  //    INDEPENDENT of POST_NOTIFICATIONS permission. (Login itself is the
  //    authoritative token-creation point via driver-login.verify_otp;
  //    this hook is the safety net for OS-driven rotations / missed
  //    inserts.)
  //  • pushNotificationReceived fires on EVERY screen and from cold start.
  useFcmRegistration(driver?.id);
  useDriverNotifications(driver?.id);
  // Clears activeTripStore when a trip is completed/cancelled externally
  // (e.g. by Admin Portal) so the Sign Out button re-enables instantly.
  useActiveTripWatcher(driver?.id);
  return null;
}

const App = () => (
    <QueryClientProvider client={queryClient}>
      <LanguageProvider>
        <TooltipProvider>
          <DriverThemeProvider>
            
            <Toaster />
            <Sonner />
            <BrowserRouter>
              <NetworkGateProvider>
              <PermissionsGateProvider>
                <DriverAuthProvider>
                  <DeviceStatusProvider>
                    <GlobalRealtimeManagers />
                    <GlobalBackNavigation />
                    <AppPermissionsGate>
                      <ErrorBoundary>
                        <AppVersionGate>
                          <AppRoutes />
                        </AppVersionGate>
                      </ErrorBoundary>
                    </AppPermissionsGate>
                    <GlobalTripOfferOverlay />
                    <SystemAnnouncementOverlay />
                  </DeviceStatusProvider>
                </DriverAuthProvider>
              </PermissionsGateProvider>
              </NetworkGateProvider>
            </BrowserRouter>
          </DriverThemeProvider>
        </TooltipProvider>
      </LanguageProvider>
    </QueryClientProvider>
);

export default App;
